import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Danger, Primary, Tertiary, TertiarySmall } from '../../../components/button/button';
import { SortableTable } from '../../../components/table/sortable-table';
import { ChildLayer, FilteredLayer, LayerTree, LayerTypes, MapLayer } from '../../geo-info/types/map-layer';
import { Actions, Content, Dialog, Title } from '../../../components/dialog/dialog';
import { Checkbox2 } from '../../../components/forms/checkbox';
import { FieldValues, useForm } from 'react-hook-form';
import { Label, TextInputLarge } from '../../../components/forms/input';
import {
  useDeleteMutation,
  usePostMutation,
  usePutMutation
} from '../../../hooks/use-mutations';
import { useQuery } from '../../../hooks/use-query';
import { LayerFilter } from '../../../widgets/filter-layers';
import { FolderFilter } from '../../../widgets/filter-folders';
import { FileInputLarge } from '../../../components/forms/file';
import { alert } from '../../../components/toast/toast';
import { usePostFormDataMutation } from '../../../hooks/use-form-mutations';

enum Modes {
  BROWSE = 'BROWSE',
  DELETE = 'DELETE',
  ADD_LAYER = 'ADD_LAYER',
  CREATE_FOLDER = 'CREATE_FOLDER',
  IMPORT_FOLDER = 'IMPORT_FOLDER',
  CREATE_FOLDER_ALIAS = 'CREATE_FOLDER_ALIAS',
  CREATE_VIRTUAL_LAYER = 'CREATE_VIRTUAL_LAYER'
}

export const Structure = (): JSX.Element => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const configId = params.configId as string;
  const layerId = searchParams.get('selectedLayerId') || params.layerId as string;
  const [selectedLayer, setSelectedLayer] = useState<ChildLayer>();
  const [pageMode, setPageMode] = useState<Modes>(Modes.BROWSE);
  const { register, handleSubmit, reset } = useForm();
  const { register: registerUpload, handleSubmit: handleUploadSubmit } = useForm();
  const layerTree = useQuery<LayerTree>({ path: `/map-layers/tree/${layerId}?configurationId=${configId}`, options: { keepPreviousData: true } });
  const postMutation = usePostMutation<MapLayer>({ path: '/map-layers' });
  const associatePostMutation = usePostMutation({ path: `/map-layers/${layerId}/child?configurationId=${configId}`, cacheKey: `/map-layers/tree/${layerId}?configurationId=${configId}` });
  const putMutation = usePutMutation({ path: `/map-layers/reorder/${layerId}`, cacheKey: `/map-layers/tree/${layerId}?configurationId=${configId}` });
  const deleteMutation = useDeleteMutation({ cacheKey: `/map-layers/tree/${layerId}?configurationId=${configId}` });
  const postFormMutation = usePostFormDataMutation({ path: `/map-layers/tree/import/${layerId}?configurationId=${configId}` });

  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>([]);
  const [rows, setRows] = useState<ChildLayer[]>([]);
  const transformedChildren = useMemo(() => {
    return layerTree.data?.children?.map(item => {
      return {
        ...item,
        id: item.childLayerId,
      };
    });
  }, [layerTree.data?.children]) || [];

  useEffect(() => {
    if (layerTree.data?.children) {
      setSelectedLayerIds(layerTree.data?.children.map(item => item.childLayerId));
    }
  }, [layerTree.data?.children]);

  const columns = [
    { accessor: '', Header: 'Sort' },
    { accessor: 'name', Header: 'Name' },
  ];

  const onDragEnd = (_: string, data: ChildLayer[]): void => {
    setRows(data);
    putMutation.mutate(data.map(item => item.childLayerId));
  };

  const onDelete = (item: ChildLayer): void => {
    setSelectedLayer(item);
    setPageMode(Modes.DELETE);
  };

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/map-layers/${layerId}/child/${selectedLayer?.childLayerId as string}?configurationId=${configId}` });
    setPageMode(Modes.BROWSE);
  };

  const onModeChange = (pageMode: Modes): () => void => (): void => {
    reset();
    setPageMode(pageMode);
  };

  const onSubmit = async (fields: FieldValues): Promise<void> => {
    setPageMode(Modes.BROWSE);
    try {
      const result = await postMutation.mutateAsync({ ...fields, configurationId: configId });
      associatePostMutation.mutate({ childLayerId: result.id });
    } catch(error) {
      console.log('error', error);
      alert.error('Failed to create layer');
    }
  };

  const onAdd = (item: FilteredLayer): void => {
    setSelectedLayerIds([...selectedLayerIds, item.id]);
    associatePostMutation.mutate({ childLayerId: item.id });
  };

  const onInspect = (item: FilteredLayer): void => {
    navigate(`/configurations/${params.configId as string}/contents/${params.layerId as string}?selectedLayerId=${item.id}`);
  };

  const onUploadFile = (fields: FieldValues): void => {
    postFormMutation.mutate(fields);
    setPageMode(Modes.BROWSE);
  };

  const isFolder = layerTree.data?.layer.type === LayerTypes.FOLDER;
  return <div>
    <div className="mb-4">
      {isFolder && (
        <>
          <TertiarySmall className="me-2" onClick={onModeChange(Modes.CREATE_FOLDER)}>Create Folder</TertiarySmall>
          <TertiarySmall className="me-2" onClick={onModeChange(Modes.CREATE_VIRTUAL_LAYER)}>Create Virtual layer</TertiarySmall>
        </>
      )}
      <TertiarySmall className="me-2" onClick={onModeChange(Modes.ADD_LAYER)}>Add Layer</TertiarySmall>
      {isFolder && (
        <>
          <TertiarySmall className="me-2" onClick={onModeChange(Modes.CREATE_FOLDER_ALIAS)}>Create Folder Alias</TertiarySmall>
          <TertiarySmall onClick={onModeChange(Modes.IMPORT_FOLDER)}>Import Folder</TertiarySmall>
        </>
      )}
    </div>
    <SortableTable
      isLoading={layerTree.isLoading}
      columns={columns}
      rows={rows?.length ? rows : transformedChildren}
      onDragEnd={onDragEnd}
      onDelete={onDelete}
      onInspect={onInspect}
    />
    {pageMode === Modes.CREATE_FOLDER && (
      <Dialog onCancel={(): void => setPageMode(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Create Folder</Title>
          <Content>
            <div className="mb-4">
              <Label name="name">
                Name
              </Label>
              <TextInputLarge {...register('name')} placeholder="Name" autoFocus />
            </div>
            <div className="mb-4">
              <Checkbox2 {...register('defaultOpen')} label="Open by default" />
            </div>
            <input type="hidden" {...register('type')} value={LayerTypes.FOLDER} />
          </Content>
          <Actions>
            <Tertiary onClick={(): void => setPageMode(Modes.BROWSE)}>Cancel</Tertiary>
            <Primary type="submit">Create new folder</Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageMode === Modes.CREATE_VIRTUAL_LAYER && (
      <Dialog onCancel={(): void => setPageMode(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Create Virtual Layer</Title>
          <Content>
            <div className="mb-4">
              <Label name="name">
                Name
              </Label>
              <TextInputLarge {...register('name')} placeholder="Name" autoFocus />
            </div>
            <input type="hidden" {...register('type')} value={LayerTypes.VIRTUAL_LAYER} />
          </Content>
          <Actions>
            <Tertiary onClick={(): void => setPageMode(Modes.BROWSE)}>Cancel</Tertiary>
            <Primary type="submit">Create layer</Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageMode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageMode(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Layer</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Tertiary onClick={(): void => setPageMode(Modes.BROWSE)}>Cancel</Tertiary>
          <Danger onClick={onDeleteConfirm}>Delete</Danger>
        </Actions>
      </Dialog>
    )}
    {pageMode === Modes.ADD_LAYER && (
      <LayerFilter
        title="Add Layer"
        onAdd={onAdd}
        selectedLayers={selectedLayerIds}
        onClose={(): void => setPageMode(Modes.BROWSE)}
      />
    )}
    {pageMode === Modes.CREATE_FOLDER_ALIAS && (
      <FolderFilter
        title="Add Folder"
        onAdd={onAdd}
        selectedLayers={selectedLayerIds}
        onClose={(): void => setPageMode(Modes.BROWSE)}
      />
    )}
    {pageMode === Modes.IMPORT_FOLDER && (
      <Dialog onCancel={(): void => setPageMode(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleUploadSubmit(onUploadFile)}>
          <Title>Import Folder</Title>
          <Content>
            <div className="mb-4">
              <Label>Choose file to upload</Label>
              <FileInputLarge { ...registerUpload('file', { required: true })} />
            </div>
          </Content>
          <Actions>
            <Tertiary onClick={(): void => setPageMode(Modes.BROWSE)}>Cancel</Tertiary>
            <Primary type="submit">Upload</Primary>
          </Actions>
        </form>
      </Dialog>
    )}
  </div>;
};
